import axios, { Canceler } from 'axios'
import {
  IStepByIdRespDto,
  IStepCancelAccountConnectionUpdateDto,
  IStepCreateDto,
  IStepsAllRawRespDto,
  IStepsRespDto,
  IStepUpdateDto,
  IStepUpdateRespDto,
} from 'src/types/step.type'
import { EnhancedWithAuthHttpService } from './http-auth.service'
import { axiosInstance } from './http-common.service'
import { HttpService } from './http.service'
import {
  ICancelRemovalDto,
  IImpactAllWithQuantityRespDto,
  IMessageResponseDto,
  IRemoveStepAccountsConnectionDto,
  IStepAccountsConnectionRespDto,
  IStepAccountsRespDto,
} from 'src/types'

class StepDataService extends EnhancedWithAuthHttpService {
  CancelToken
  cancel?: Canceler
  cancelAllRawSteps?: Canceler
  cancelAccountStepsConnections?: Canceler

  constructor() {
    super(new HttpService(axiosInstance))
    this.CancelToken = axios.CancelToken
  }
  createStep(dto: IStepCreateDto) {
    return this.post<string, IStepCreateDto>(`steps`, dto)
  }

  getStepById(id: string, locale: string) {
    return this.get<IStepByIdRespDto>(`steps/${id}/${locale}`)
  }

  editStep(dto: IStepUpdateDto, id: string) {
    return this.put<IStepUpdateRespDto, IStepUpdateDto>(`steps/${id}`, dto)
  }

  removeSteps(ids: string[]) {
    return this.delete<string>(`steps`, { data: { ids } })
  }

  cancelRemove(params: ICancelRemovalDto) {
    return this.get<IMessageResponseDto>('steps/cancel-deletion', { params })
  }

  cancelStepAccountConnectionUpdate(dto: IStepCancelAccountConnectionUpdateDto) {
    return this.get<IMessageResponseDto>(
      `steps/connections/account/step/cancel?connection_id=${dto.connectionId}&id=${dto.id}`,
    )
  }

  getSteps(params?: {}) {
    return this.get<IStepsRespDto>(`steps`, { params })
  }

  getStepImpacts(stepId: string, params?: {}) {
    return this.get<IImpactAllWithQuantityRespDto>(`steps/${stepId}/impacts`, { params })
  }

  getAllRawSteps(params?: {}) {
    if (this.cancelAllRawSteps) {
      this.cancelAllRawSteps()
    }

    return this.get<IStepsAllRawRespDto>(`steps/all/raw`, {
      params,
      cancelToken: new this.CancelToken((c) => {
        this.cancelAllRawSteps = c
      }),
    })
  }

  getFilteredSteps(params?: {}) {
    if (this.cancel) {
      this.cancel()
    }

    return this.get<IStepsRespDto>(`steps`, {
      params,
      cancelToken: new this.CancelToken((c) => {
        this.cancel = c
      }),
    })
  }

  getAccountStepsConnections(params?: {}) {
    return this.get<IStepAccountsRespDto>(`steps/connections/account`, {
      params,
    })
  }

  getStepAccountsConnections(stepId?: string) {
    return this.get<IStepAccountsConnectionRespDto[]>(`steps/connections/account/step`, {
      params: { stepId },
    })
  }

  removeAccountConnections(data: IRemoveStepAccountsConnectionDto) {
    return this.delete<IMessageResponseDto>(`steps/connections/account`, {
      data,
    })
  }

  approveAccountConnection(token: string) {
    return this.put<IMessageResponseDto & { accountId: string }, {}>(
      `steps/connections/account/${token}`,
      {},
    )
  }

  approveAccountConnectionManually(connectionId: string) {
    return this.put<IMessageResponseDto & { accountId: string }, {}>(
      `steps/connections/account/approve`,
      {},
      { params: { connectionId } },
    )
  }

  checkCodeExist(code: string, id?: string, accountId?: string) {
    return this.get<IMessageResponseDto>(`steps/code/${code}`, {
      params: { accountId, id },
    })
  }

  getInitialCode(accountId?: string) {
    return this.get<string>(`steps/code/initial`, { params: { accountId } })
  }
}

const StepService = new StepDataService()
export default StepService
